import { requestWrapper } from '@/utils/request';
import { CreateUserMemberCardParams } from './member';
import {
  OrderDetailResponse,
  OrderInfo,
  OrderLog,
  OrderPaymentRecord,
  OrderPayType,
  OrderRefundRecord,
  OrderStatus,
  OrderType,
  PaymentInfo,
} from '@/types/order';
import { ListResponse } from '@/types/common';

// 创建订单参数
type CreateOrderParams = {
  original_amount: number; // 原价
} & CreateUserMemberCardParams;

// 获取订单列表参数
export type GetOrderListParams = {
  page: number;
  page_size: number;
  store_id?: number;
  /** 订单状态：单值、-1 表示全部，或多选时的逗号分隔字符串 */
  status?: OrderStatus | -1 | string;
  user_id?: number; // 用户ID
  start_time?: string; // 开始时间
  end_time?: string; // 结束时间
  order_type?: OrderType; // 商品类型：会员卡 / 课次券 / 线上课
};

// 导出订单列表参数（复用筛选条件，不含分页字段）
export type ExportOrderListParams = Omit<GetOrderListParams, 'page' | 'page_size'>;

// 创建用户会员卡订单
export async function createMemberCardOrder(body: Partial<CreateOrderParams>) {
  return requestWrapper<{ order_no: string }>('/api/user_member_card/create_order', {
    method: 'POST',
    data: body,
    showError: false,
  });
}

// 获取订单列表
export async function getOrderList(params: GetOrderListParams) {
  const mergedParams = {
    ...params,
    status: params.status ? params.status : -1, // -1 表示查询全部状态
  };
  return requestWrapper<ListResponse<OrderInfo>>('/api/order/list', {
    method: 'GET',
    params: mergedParams,
  });
}

/**
 * 导出线上支付订单
 * 约定：返回 { url } 下载地址
 */
export async function exportOrderList(params: ExportOrderListParams) {
  const mergedParams = {
    ...params,
    status: params.status ? params.status : -1,
  };
  return requestWrapper<{ url: string }>('/api/order/export', {
    method: 'GET',
    params: mergedParams,
  });
}

// 获取订单详情
export async function getOrderDetail(order_no: string) {
  return requestWrapper<OrderDetailResponse>('/api/order/detail', {
    method: 'GET',
    params: { order_no },
  });
}

// 获取订单支付记录
export async function getOrderPaymentRecord(order_no: string) {
  return requestWrapper<ListResponse<OrderPaymentRecord>>('/api/order/payment_transaction', {
    method: 'GET',
    params: { order_no },
  });
}

// 获取订单退款记录
export async function getOrderRefundRecord(order_no: string) {
  return requestWrapper<ListResponse<OrderRefundRecord>>('/api/order/refund_list', {
    method: 'GET',
    params: { order_no },
  });
}

// 获取订单日志
export async function getOrderLog(order_no: string) {
  return requestWrapper<ListResponse<OrderLog>>('/api/order/log', {
    method: 'GET',
    params: { order_no },
  });
}

// 订单关闭
export async function closeOrder(order_no: string) {
  return requestWrapper('/api/order/close', {
    method: 'POST',
    data: { order_no },
  });
}

// 订单退款
export async function refundOrder(body: { order_no: string; remark: string }) {
  return requestWrapper('/api/order/refund', {
    method: 'POST',
    data: body,
  });
}

// 订单更新
export async function updateOrder(body: { order_no: string; pay_amount: number }) {
  return requestWrapper('/api/order/update', {
    method: 'POST',
    data: body,
  });
}

// 获取支付信息
export async function getPaymentInfo(body: {
  order_no: string;
  pay_type: OrderPayType; // 支付方式
}) {
  return requestWrapper<PaymentInfo>('/api/order/create_outtrade', {
    method: 'POST',
    data: body,
  });
}
